import { Box, Stack } from "@mui/material";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useCallback, useMemo, useState } from "react";

import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import {
  type PartnerListItemDto,
  PartnerListSortColumn,
  PartnerStatus,
  SortOrder,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { usePartnersQuery } from "Partners/Api/Queries/usePartnersQuery";
import {
  type FlatPartner,
  PARTNERS_SORTABLE_COLUMNS,
  usePartnersTableColumns,
} from "Partners/Components/usePartnersTableColumns";
import { TableSearchInput } from "Projects/Components/TableSearchInput";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlDropdown } from "Shared/Inputs/BlDropdown";

const SORT_COLUMN_MAP: Partial<Record<string, PartnerListSortColumn>> = {
  companyName: PartnerListSortColumn.CompanyName,
  projectCount: PartnerListSortColumn.ProjectCount,
  email: PartnerListSortColumn.Email,
  phone: PartnerListSortColumn.Phone,
};

export const PartnersTable = (): React.ReactNode => {
  const { t } = useResource();

  const [expandedPartners, setExpandedPartners] = useState<Set<string>>(
    new Set(),
  );

  const [selectedStatus, setSelectedStatus] = useQueryState(
    "status",
    parseAsStringEnum<PartnerStatus>(Object.values(PartnerStatus)),
  );

  const {
    search,
    debouncedSearch,
    onSearchChange,
    pageIndex,
    pageSize,
    onPaginationChange,
    resetPage,
    sorting,
    onSortingChange,
  } = useTableSearchParams({ validSortIds: [...PARTNERS_SORTABLE_COLUMNS] });

  const sortBy = sorting[0] ? SORT_COLUMN_MAP[sorting[0].id] : undefined;
  const sortOrder = sorting[0]?.desc
    ? SortOrder.Descending
    : SortOrder.Ascending;

  const { data, isLoading } = usePartnersQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    searchQuery: debouncedSearch || null,
    sortBy,
    sortOrder,
    statusFilter: selectedStatus ? [selectedStatus] : null,
  });

  const flatPartners = useMemo((): FlatPartner[] => {
    const result: FlatPartner[] = [];
    const flatten = (
      partners: PartnerListItemDto[],
      parentID: string | null,
    ) => {
      for (const partner of partners) {
        result.push({ ...partner, parentPartnerID: parentID });
        if (partner.childPartners?.length) {
          flatten(partner.childPartners, partner.partnerID);
        }
      }
    };
    flatten(data?.items ?? [], null);
    return result;
  }, [data]);

  const togglePartnerExpansion = useCallback((partnerId: string) => {
    setExpandedPartners((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(partnerId)) {
        newSet.delete(partnerId);
      } else {
        newSet.add(partnerId);
      }
      return newSet;
    });
  }, []);

  const hasSubPartners = useCallback(
    (partnerId: string) =>
      flatPartners.some((p) => p.parentPartnerID === partnerId),
    [flatPartners],
  );

  const getSubPartners = useCallback(
    (parentId: string) =>
      flatPartners.filter((p) => p.parentPartnerID === parentId),
    [flatPartners],
  );

  const visiblePartners = useMemo(() => {
    const result: FlatPartner[] = [];
    for (const parent of flatPartners.filter(
      (p) => p.parentPartnerID === null,
    )) {
      result.push(parent);
      if (expandedPartners.has(parent.partnerID)) {
        result.push(...getSubPartners(parent.partnerID));
      }
    }
    return result;
  }, [flatPartners, expandedPartners, getSubPartners]);

  const columns = usePartnersTableColumns(
    expandedPartners,
    togglePartnerExpansion,
    hasSubPartners,
  );

  const table = useReactTable({
    data: visiblePartners,
    columns,
    getRowId: (row) => row.partnerID,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: data?.totalCount ?? 0,
    state: { pagination: { pageIndex, pageSize }, sorting },
    onSortingChange,
    onPaginationChange,
  });

  const statusOptions = [
    { value: "", label: t(Resources.Common.AllStatuses) },
    { value: PartnerStatus.Active, label: t(Resources.Common.StatusActive) },
    {
      value: PartnerStatus.Inactive,
      label: t(Resources.Common.StatusInactive),
    },
  ];

  return (
    <Box>
      <Stack
        direction="row"
        spacing={Spacing.S}
        sx={{ marginBottom: Spacing.M }}
      >
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            name="partner-search"
            value={search}
            onChange={onSearchChange}
            placeholder={t(Resources.Partners.SearchPartnersPlaceholder)}
          />
        </Box>
        <Box sx={{ minWidth: 180 }}>
          <BlDropdown
            name="status-filter"
            options={statusOptions}
            value={selectedStatus ?? ""}
            onChange={(value) => {
              setSelectedStatus((value as PartnerStatus) || null);
              resetPage();
            }}
            placeholder={t(Resources.Common.AllStatuses)}
          />
        </Box>
      </Stack>
      <BlTanstackTable table={table} isLoading={isLoading} />
    </Box>
  );
};
